import { applyOfferStages, candidateServices, candidatesForService, daysInQuery, houseRulesApply, minUsefulGapMin, } from './availability.js';
import { effectiveOfferPolicy, relaxationLadder } from './offers.js';
const MINUTE = 60_000;
/**
 * Explain one mode.
 *
 * `mode` defaults to the context's configured one, so a caller that just wants
 * "what would this tenant do" passes two arguments. The simulator passes all
 * three, once per column.
 *
 * The relaxation ladder runs here too, for the same reason it runs in
 * `slotsInWindowWithPolicy`: an operator looking at an empty *Touching* column
 * needs to see that the engine would in fact have answered — and with what —
 * rather than concluding that the mode means "closed".
 */
export function explainOffers(ctx, query, mode) {
    const houseRules = houseRulesApply(query);
    const policy = effectiveOfferPolicy(ctx.offer_policy, houseRules);
    const asked = mode ?? policy.mode;
    const services = candidateServices(ctx, query);
    const days = daysInQuery(ctx, query);
    const gapMin = minUsefulGapMin(ctx);
    const empty = {
        mode: asked,
        policy_applied: asked,
        relaxed: false,
        min_useful_gap_min: gapMin,
        offers: [],
        kept: 0,
        lead_count: 0,
    };
    if (services.length === 0 || days.length === 0)
        return empty;
    const candidates = services.flatMap((s) => 
    // `resource_id` is deliberately not threaded: the simulator explains the
    // *house's* offer policy, and narrowing to one person is a different
    // question. The option and the add-ons are threaded, because they change
    // the **span** — and a simulator explaining why a 30-minute facial fits a
    // gap that a 120 does not would be explaining the wrong ask.
    candidatesForService(ctx, s, days, query.limit, query.party_size, houseRules, undefined, query));
    candidates.sort((a, b) => a.slot.start.localeCompare(b.slot.start) ||
        a.slot.service_id.localeCompare(b.slot.service_id) ||
        a.slot.resource_id.localeCompare(b.slot.resource_id));
    const ladder = relaxationLadder(asked);
    let result = applyOfferStages(ctx, query, candidates, query.limit, asked, gapMin * MINUTE);
    for (const rung of ladder.slice(1)) {
        if (result.slots.length > 0)
            break;
        result = applyOfferStages(ctx, query, candidates, query.limit, rung, gapMin * MINUTE);
    }
    const top = houseRules && policy.rank !== undefined
        ? query.audience === 'partner'
            ? policy.top_n?.partner
            : policy.top_n?.guest
        : undefined;
    // `lead` follows the *ranked* order, because that is the order a surface
    // would read them out in — the first N of the packing order under
    // `rank: 'packing'`, the first N chronologically otherwise.
    const keptDecisions = result.decisions.filter((d) => d.kept);
    const ranked = policy.rank === 'packing'
        ? [...keptDecisions].sort((a, b) => b.score - a.score || a.candidate.slot.start.localeCompare(b.candidate.slot.start))
        : keptDecisions;
    const leading = new Set(ranked.slice(0, top === undefined ? ranked.length : top).map((d) => d.candidate.slot));
    return {
        mode: asked,
        policy_applied: result.mode,
        relaxed: result.mode !== asked,
        min_useful_gap_min: gapMin,
        kept: keptDecisions.length,
        lead_count: Math.min(top === undefined ? keptDecisions.length : top, keptDecisions.length),
        offers: result.decisions.map((decision) => ({
            start: decision.candidate.slot.start,
            end: decision.candidate.slot.end,
            resource_id: decision.candidate.slot.resource_id,
            service_id: decision.candidate.slot.service_id,
            kept: decision.kept,
            lead: decision.kept && leading.has(decision.candidate.slot),
            reason: decision.reason,
            score: decision.score,
            gap_before_min: decision.candidate.geometry.gap_before_ms / MINUTE,
            gap_after_min: decision.candidate.geometry.gap_after_ms / MINUTE,
            abuts_before: decision.abuts_before,
            abuts_after: decision.abuts_after,
            anchor: decision.candidate.geometry.anchor,
            pooled: decision.candidate.geometry.pooled,
        })),
    };
}
/**
 * All three columns at once — what the simulator draws.
 *
 * Strictest first, so the operator reads left to right from "everything" to
 * "only the jigsaw pieces": `open`, `no_slivers`, `touching`.
 */
export function explainEveryMode(ctx, query) {
    return {
        open: explainOffers(ctx, query, 'open'),
        no_slivers: explainOffers(ctx, query, 'no_slivers'),
        touching: explainOffers(ctx, query, 'touching'),
    };
}
//# sourceMappingURL=explain.js.map